<script>
  import { dragHandle, dragHandleZone, SHADOW_ITEM_MARKER_PROPERTY_NAME } from 'svelte-dnd-action';
  import { Check, Eye, EyeOff, GripVertical, RotateCcw, Save } from 'lucide-svelte';
  import {
    applyCallToolbarPreset,
    CALL_TOOLBAR_ACTIONS,
    CALL_TOOLBAR_PRESETS,
    normalizeCallToolbarConfig
  } from './call-toolbar-config.js';

  export let config;
  export let mode = 'video';
  export let saving = false;
  export let resetting = false;
  export let canSave = false;
  export let canReset = false;
  export let onChange = () => {};
  export let onSave = () => {};
  export let onReset = () => {};

  let items = [];
  let isDragging = false;

  $: normalizedConfig = normalizeCallToolbarConfig(config);
  $: if (!isDragging) {
    items = decorateActions(normalizedConfig.actions);
  }

  function decorateActions(actions = []) {
    return actions.map((item) => ({
      ...CALL_TOOLBAR_ACTIONS.find(({ id }) => id === item.id),
      ...item
    }));
  }

  function update(next) {
    config = normalizeCallToolbarConfig(next);
    onChange?.(config);
  }

  function selectPreset(presetId) {
    update(applyCallToolbarPreset(config, presetId));
  }

  function selectStyle(style) {
    update({ ...config, preset: 'custom', style });
  }

  function toggleAction(action) {
    if (action.locked) return;
    update({
      ...config,
      preset: 'custom',
      actions: config.actions.map((item) => item.id === action.id ? { ...item, visible: !item.visible } : item)
    });
  }

  function handleConsider(event) {
    isDragging = true;
    items = decorateActions(event.detail.items);
  }

  function handleFinalize(event) {
    isDragging = false;
    items = decorateActions(event.detail.items);
    const reordered = items.map(({ id, visible }) => ({ id, visible }));
    update({ ...config, preset: 'custom', actions: reordered });
  }
</script>

<section class="toolbar-settings">
  <div class="section-heading">
    <div><strong>Bố cục yêu thích</strong><span>Chọn mẫu để thiết lập nhanh thanh điều khiển.</span></div>
  </div>

  <div class="preset-grid">
    {#each CALL_TOOLBAR_PRESETS as preset}
      <button class:active={config.preset === preset.id} onclick={() => selectPreset(preset.id)}>
        <span>{preset.label}</span>
        <small>{preset.description}</small>
        {#if config.preset === preset.id}<span class="preset-check"><Check size={14} /></span>{/if}
      </button>
    {/each}
  </div>

  <div class="section-heading">
    <div><strong>Kiểu hiển thị</strong><span>Thay đổi mật độ và cách trình bày nút.</span></div>
  </div>

  <div class="style-selector">
    <button class:active={config.style === 'labels'} onclick={() => selectStyle('labels')}>Có nhãn</button>
    <button class:active={config.style === 'compact'} onclick={() => selectStyle('compact')}>Gọn</button>
    <button class:active={config.style === 'glass'} onclick={() => selectStyle('glass')}>Trong suốt</button>
  </div>

  <div class="section-heading">
    <div><strong>Sắp xếp thao tác</strong><span>Giữ tay nắm để kéo; nhấn biểu tượng mắt để ẩn hoặc hiện.</span></div>
  </div>

  <div
    class="action-list"
    use:dragHandleZone={{
      items,
      flipDurationMs: 150,
      dropTargetStyle: {
        outline: '1px solid rgba(96, 165, 250, 0.78)',
        outlineOffset: '-1px',
        borderRadius: '0.9rem',
        background: 'rgba(59, 130, 246, 0.08)'
      }
    }}
    onconsider={handleConsider}
    onfinalize={handleFinalize}>
    {#each items as action (action.id)}
      <article
        class:unsupported={!action.modes.includes(mode)}
        class:drop-placeholder={Boolean(action[SHADOW_ITEM_MARKER_PROPERTY_NAME])}>
        <button type="button" class="drag-handle" aria-label={`Di chuyển ${action.label}`} title="Kéo để sắp xếp" use:dragHandle><GripVertical size={16} /></button>
        <div>
          <strong>{action.label}</strong>
          <small>{action.modes.includes(mode) ? action.description : 'Không áp dụng cho kiểu cuộc gọi hiện tại'}</small>
        </div>
        <button
          class:locked={action.locked}
          class:visible={action.visible}
          aria-label={action.visible ? `Ẩn ${action.label}` : `Hiện ${action.label}`}
          title={action.locked ? 'Thao tác quan trọng luôn hiển thị' : action.visible ? 'Ẩn thao tác' : 'Hiện thao tác'}
          onclick={() => toggleAction(action)}>
          {#if action.visible}<Eye size={16} />{:else}<EyeOff size={16} />{/if}
        </button>
      </article>
    {/each}
  </div>

  <footer>
    <button class="secondary" disabled={saving || resetting || !canReset} onclick={onReset}>
      <RotateCcw size={15} />
      <span>{resetting ? 'Đang về mặc định...' : 'Về mặc định'}</span>
    </button>
    <button class="primary" disabled={saving || resetting || !canSave} onclick={onSave}>
      <Save size={15} />
      <span>{saving ? 'Đang lưu...' : 'Lưu thiết lập'}</span>
    </button>
  </footer>
</section>

<style>
  .toolbar-settings { display:grid; gap:.75rem; }
  .toolbar-settings {
    min-height: 0;
  }
  .toolbar-settings {
    scrollbar-width: thin;
    scrollbar-color: rgba(96, 165, 250, 0.55) rgba(15, 23, 42, 0.35);
  }
  .toolbar-settings::-webkit-scrollbar {
    width: 0.52rem;
  }
  .toolbar-settings::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.28);
    border-radius: 999px;
  }
  .toolbar-settings::-webkit-scrollbar-thumb {
    border: 0.14rem solid transparent;
    background: linear-gradient(180deg, rgba(96, 165, 250, 0.7), rgba(37, 99, 235, 0.88));
    border-radius: 999px;
    background-clip: padding-box;
  }
  .section-heading strong,.section-heading span { display:block; }
  .section-heading strong { color:#f8fafc; font-size:.68rem; }
  .section-heading span { margin-top:.15rem; color:#94a3b8; font-size:.54rem; line-height:1.45; }
  .preset-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:.4rem; }
  .preset-grid button { position:relative; min-height:4.4rem; padding:.62rem; border:1px solid rgba(255,255,255,.09); border-radius:.8rem; text-align:left; background:rgba(255,255,255,.04); }
  .preset-grid button.active { border-color:rgba(96,165,250,.42); background:rgba(37,99,235,.18); }
  .preset-grid span,.preset-grid small { display:block; }
  .preset-grid span { color:#e2e8f0; font-size:.61rem; font-weight:750; }
  .preset-grid small { margin-top:.2rem; color:#94a3b8; font-size:.49rem; line-height:1.4; }
  .preset-grid .preset-check { position:absolute; top:.42rem; right:.42rem; display:grid; place-items:center; color:#93c5fd; }
  .style-selector { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:.3rem; padding:.25rem; border-radius:.75rem; background:rgba(2,6,23,.4); }
  .style-selector button { padding:.48rem .3rem; border-radius:.58rem; color:#94a3b8; font-size:.55rem; font-weight:700; }
  .style-selector button.active { color:#eff6ff; background:rgba(59,130,246,.28); }
  .action-list { display:grid; gap:.35rem; padding:.18rem; border-radius:.9rem; transition:background 140ms ease, outline-color 140ms ease; }
  .action-list :global(.dndDraggingSource) { opacity:.96; box-shadow:0 18px 36px rgba(15,23,42,.35); }
  article { display:grid; grid-template-columns:auto minmax(0,1fr) auto; align-items:center; gap:.5rem; padding:.48rem; border:1px solid rgba(255,255,255,.08); border-radius:.72rem; background:rgba(255,255,255,.035); }
  article.drop-placeholder {
    visibility:visible !important;
    border:1px solid rgba(96,165,250,.98);
    background:linear-gradient(180deg, rgba(219,234,254,.3), rgba(96,165,250,.2));
    box-shadow:inset 0 0 0 1px rgba(219,234,254,.48), 0 8px 24px rgba(37,99,235,.16);
  }
  article.drop-placeholder > * { opacity:.22; }
  article.unsupported { opacity:.5; }
  article strong,article small { display:block; }
  article strong { color:#e2e8f0; font-size:.59rem; }
  article small { margin-top:.12rem; color:#94a3b8; font-size:.49rem; line-height:1.35; }
  article button { display:grid; place-items:center; width:1.9rem; height:1.9rem; border-radius:.58rem; color:#64748b; background:rgba(148,163,184,.08); }
  article button.visible { color:#93c5fd; background:rgba(59,130,246,.14); }
  article button.locked { color:#cbd5e1; cursor:not-allowed; opacity:.65; }
  .drag-handle { cursor:grab; touch-action:none; }
  footer { display:flex; justify-content:flex-end; gap:.45rem; padding-top:.25rem; }
  footer button { display:flex; align-items:center; justify-content:center; gap:.35rem; padding:.58rem .72rem; border-radius:.7rem; font-size:.56rem; font-weight:750; }
  footer .secondary { color:#cbd5e1; background:rgba(255,255,255,.07); }
  footer .primary { color:white; background:#2563eb; box-shadow:0 8px 22px rgba(37,99,235,.25); }
  footer button.primary:disabled { background:rgba(37,99,235,.55); }
  footer button.secondary:disabled { background:rgba(255,255,255,.05); }
  footer button:disabled { opacity:.55; cursor:wait; }
  @media(max-width:520px){ .preset-grid{grid-template-columns:1fr}.preset-grid button{min-height:auto}.style-selector{grid-template-columns:1fr} }
</style>
